import { TokenValue } from "@@/design-tokens/breakpoints";
import Scrollbar from "@@/components/Scrollbar";
import Box from "@@/elements/Box";
import { ChevronDown } from "@@/elements/Icons/icons";
import Typhography from "@@/elements/Typography";
import cn from "classnames";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ClearButton from "./Options/clearButton";
import DropdownOption, { DropdownOptionProps } from "./Options/option";
import styles from "./styles.module.scss";

type DropdownProps = {
  placeholder: string;
  clearButtonText: string;
  options: DropdownOptionProps[];
  selectedValues: string[];
  setSelectedValues: Function,
  disabled?: boolean;
  fullWidth?: boolean;
  singleSelect?: boolean;
  dense?: boolean;
  truncation?: boolean;
};

const CLEAR_BUTTON_ID = "dropdown-clear";

const Dropdown = ({
  placeholder,
  clearButtonText,
  options,
  selectedValues,
  setSelectedValues,
  disabled,
  fullWidth,
  singleSelect = false,
  dense,
  truncation,
}: DropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRef = useRef<HTMLDivElement>(null);
  const dropdownOptionsRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [minWidth, setMinWidth] = useState("100px");
  const [maxWidth, setMaxWidth] = useState("400px");
  const [maxHeight, setMaxHeight] = useState("0px");
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [activeIndex, setActiveIndex] = useState(-1);

  const itemCount = options.length + 1;

  const getItemId = (index: number) => {
    if (index === 0) return CLEAR_BUTTON_ID;
    return `dropdown-option-${options[index - 1].value}`;
  };

  const MIN_HEIGHT = 160;
  const MAX_HEIGHT = 600;

  const handleChange = (value : string) => {
    if (value === null) {
      setSelectedValues([]);
      return;
    }
    if (singleSelect) {
      setSelectedValues([value]);
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    const newValues = [...selectedValues];
    if (newValues.includes(value)) {
      newValues.splice(newValues.indexOf(value), 1);
    } else {
      newValues.push(value);
    }
    setSelectedValues(newValues);
  };

  const updateMenuGeometry = () => {
    if (dropdownRef.current && open) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom, left: rect.left });
      const height = window.innerHeight - rect.bottom - 25;
      if (height < MIN_HEIGHT) setMaxHeight(`${MIN_HEIGHT}px`);
      else if (height > MAX_HEIGHT) setMaxHeight(`${MAX_HEIGHT}px`);
      else setMaxHeight(`${height}px`);
    } else {
      setMaxHeight("0px");
    }
  };

  const handleScroll = () => {
    if (open) {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  const toggleMenu = () => {
    if (!disabled) {
      setOpen(!open);
      if (open) setActiveIndex(-1);
    }
  };

  const selectActiveItem = () => {
    if (activeIndex === 0) {
      handleChange(null);
    } else if (activeIndex > 0) {
      const option = options[activeIndex - 1];
      if (!option.disabled) {
        handleChange(option.value);
      }
    }
  };

  const nextEnabledIndex = (from: number, direction: 1 | -1): number => {
    let next = from + direction;
    while (next >= 0 && next < itemCount) {
      if (next === 0) return next;
      if (!options[next - 1].disabled) return next;
      next += direction;
    }
    return from;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        if (!open) {
          setOpen(true);
          setActiveIndex(0);
        } else {
          setActiveIndex((prev) => nextEnabledIndex(prev, 1));
        }
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        if (!open) {
          setOpen(true);
          setActiveIndex(itemCount - 1);
        } else {
          setActiveIndex((prev) => nextEnabledIndex(prev, -1));
        }
        break;
      }
      case "Enter": {
        e.preventDefault();
        if (!open) {
          setOpen(true);
          setActiveIndex(0);
        } else {
          selectActiveItem();
        }
        break;
      }
      case " ": {
        e.preventDefault();
        if (!open) {
          setOpen(true);
          setActiveIndex(0);
        } else {
          selectActiveItem();
        }
        break;
      }
      case "Escape": {
        if (open) {
          e.preventDefault();
          setOpen(false);
          setActiveIndex(-1);
          dropdownButtonRef.current?.focus();
        }
        break;
      }
      case "Home": {
        if (open) {
          e.preventDefault();
          setActiveIndex(0);
        }
        break;
      }
      case "End": {
        if (open) {
          e.preventDefault();
          setActiveIndex(itemCount - 1);
        }
        break;
      }
      case "Tab": {
        if (open) {
          setOpen(false);
          setActiveIndex(-1);
        }
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (activeIndex >= 0 && open) {
      const id = getItemId(activeIndex);
      const el = document.getElementById(id);
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  useEffect(() => {
    const updateWidth = () => {
      if (dropdownRef.current) {
        setMinWidth(`${dropdownRef.current.offsetWidth}px`);
        if (dropdownRef.current.offsetWidth > 400 || window.innerWidth < TokenValue.Medium) {
          setMaxWidth(`${dropdownRef.current.offsetWidth}px`);
        } else setMaxWidth("400px");
      }
    };

    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target)
        && dropdownOptionsRef.current && !dropdownOptionsRef.current.contains(event.target)
      ) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", updateWidth);
    updateWidth();

    if (open) {
      window.addEventListener("resize", updateMenuGeometry);
      window.addEventListener("scroll", handleScroll);
      updateMenuGeometry();
    } else {
      setMaxHeight("0px");
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", updateWidth);
      window.removeEventListener("resize", updateMenuGeometry);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [dropdownRef, open]);

  const dropdownClassName = cn(
    styles.dropdown,
    { [styles.dropdown__fullwidth]: fullWidth },
  );

  const dropdownButtonClassName = cn(
    styles.dropdown_button,
    { [styles.dropdown_button__disabled]: disabled },
  );

  const iconClassName = cn(
    styles.chevron_icon,
    { [styles.chevron_icon__open]: open },
  );

  const selectedValuesWithLabel = () => {
    const selectedValuesFromOptions = selectedValues.map((val) => {
      if (!options) return null;
      const match = options.find((option) => option.value === val);
      if (match) return match.label;
      return null;
    });

    const selectedValuesWithOptionsFiltered = selectedValuesFromOptions.filter((option) => !!option);
    return selectedValuesWithOptionsFiltered;
  };

  const amountSelectedString = `${selectedValuesWithLabel().length > 0 ? `(${selectedValuesWithLabel().length})` : ""}`;

  const showDropdown = (options && options.length) || disabled;

  if (!showDropdown) return <></>;

  const activeDescendantId = activeIndex >= 0 ? getItemId(activeIndex) : undefined;

  return (
    <Box
      className={dropdownClassName}
      ref={dropdownRef}
    >
      <Box
        className={dropdownButtonClassName}
        ref={dropdownButtonRef}
        py={dense ? 1 : 3}
        px={4}
        onClick={toggleMenu}
        onKeyDown={handleKeyDown}
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        role="combobox"
        tabIndex={0}
        aria-expanded={open}
        aria-disabled={disabled}
        aria-haspopup="listbox"
        aria-activedescendant={activeDescendantId}
      >
        <Box
          alignItems="center"
          width="full"
          className={cn(styles.dropdown_button__text, {
            [styles.dropdown_button__text_truncation]: truncation,
          })}
        >
          <Typhography
            variant="link"
            asElement="span"
            py={1}
            className={cn({
              [styles.dropdown_button__text__placeholder_truncation]: truncation,
              [styles.dropdown_button__text__placeholder_hidden]: selectedValuesWithLabel()?.length > 0,
            })}
          >
            {placeholder}
          </Typhography>
          <Box
            py={1}
            className={styles.dropdown_button__selectedvalues}>
            <Typhography
              variant="link"
              asElement="span"
            >
              {selectedValuesWithLabel().toString()}
            </Typhography>
            {!!amountSelectedString.length && <Box
              alignItems="center"
              width={4}>
              <Typhography
                className={styles.count}
                variant="link"
                asElement="div">
                {amountSelectedString}
              </Typhography>
            </Box>}
          </Box>
        </Box>

        <ChevronDown className={iconClassName} />
      </Box>
      {typeof document !== "undefined" && createPortal(
        <div
          className={cn(styles.dropdown_menu_wrapper, {
            [styles.dropdown_menu_wrapper__open]: open,
          })}
          style={{
            minWidth,
            maxWidth,
            maxHeight,
            top: menuPosition.top,
            left: menuPosition.left,
          }}
          ref={dropdownOptionsRef}
          inert={!open ? "" : undefined}
        >
          {!disabled && (
            <Scrollbar style={{ maxHeight: `calc(${maxHeight} - 20px)` }} tabindex={-1}>
              <Box
                flexDirection="column"
                role="listbox"
                aria-label="dropdown-list"
              >
                <ClearButton
                  id={CLEAR_BUTTON_ID}
                  isActive={activeIndex === 0}
                  text={clearButtonText}
                  handleChange={handleChange}
                  disabled={selectedValues.length === 0} />
                {options.map(({
                  label, value, count, disabled: optionDisabled,
                }, index) => (
                  <DropdownOption
                    key={value}
                    id={`dropdown-option-${value}`}
                    isActive={activeIndex === index + 1}
                    label={label}
                    value={value}
                    count={count}
                    disabled={optionDisabled}
                    handleChange={handleChange}
                    checked={selectedValues.includes(value)}
                    singleSelect={singleSelect}
                  />
                ))}
              </Box>
            </Scrollbar>
          )}
        </div>,
        document.body,
      )}
    </Box>
  );
};

export default Dropdown;
